import { useMemo } from 'react';
import type { ViewMode } from '../types';

interface PreviewPaneProps {
  html: string;
  viewMode: ViewMode;
}

const VIEW_STYLES: Record<ViewMode, string> = {
  standard: `
    body { font-family: system-ui, -apple-system, sans-serif; line-height: 1.6; padding: 2rem; color: #1f2937; }
    h1, h2, h3, h4, h5, h6 { margin: 1.5rem 0 0.5rem; font-weight: 600; }
    h1 { font-size: 2rem; } h2 { font-size: 1.5rem; } h3 { font-size: 1.25rem; }
    p { margin: 1rem 0; } ul, ol { padding-left: 1.5rem; }
    code { background: #f3f4f6; padding: 0.2em 0.4em; border-radius: 4px; font-size: 0.9em; }
    pre { background: #1f2937; color: #e5e7eb; padding: 1rem; border-radius: 8px; overflow-x: auto; }
    pre code { background: transparent; padding: 0; }
    blockquote { border-left: 4px solid #3b82f6; padding-left: 1rem; margin: 1rem 0; color: #6b7280; }
    a { color: #3b82f6; } table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #e5e7eb; padding: 0.5rem; text-align: left; }
    th { background: #f9fafb; }
  `,
  github: `
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; line-height: 1.5; padding: 2rem; color: #24292f; max-width: 980px; }
    h1, h2 { border-bottom: 1px solid #d0d7de; padding-bottom: 0.3em; }
    h1 { font-size: 2em; } h2 { font-size: 1.5em; }
    code { background: rgba(175,184,193,0.2); padding: 0.2em 0.4em; border-radius: 6px; font-size: 85%; }
    pre { background: #f6f8fa; padding: 16px; border-radius: 6px; overflow-x: auto; line-height: 1.45; }
    pre code { background: transparent; padding: 0; font-size: 100%; }
    blockquote { border-left: 4px solid #d0d7de; padding: 0 1em; color: #57606a; }
    a { color: #0969da; } table { border-collapse: collapse; }
    th, td { border: 1px solid #d0d7de; padding: 6px 13px; }
    th { background: #f6f8fa; font-weight: 600; }
  `,
  email: `
    body { font-family: Arial, Helvetica, sans-serif; line-height: 1.5; padding: 20px; color: #333; max-width: 600px; margin: 0 auto; }
    h1 { font-size: 24px; } h2 { font-size: 20px; } h3 { font-size: 16px; }
    p { margin: 10px 0; } a { color: #0066cc; }
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #ddd; padding: 8px; }
  `,
  raw: `body { font-family: monospace; white-space: pre-wrap; padding: 1rem; color: #1f2937; background: #fff; }`,
};

export function PreviewPane({ html, viewMode }: PreviewPaneProps) {
  const srcDoc = useMemo(() => {
    if (viewMode === 'raw') {
      return `<!DOCTYPE html>
        <html><head><style>${VIEW_STYLES.raw}</style></head>
        <body>${html.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</body></html>`;
    }
    return `<!DOCTYPE html>
      <html><head><meta charset="utf-8"><style>${VIEW_STYLES[viewMode]}</style></head>
      <body>${html}</body></html>`;
  }, [html, viewMode]);

  return (
    <div className="h-full bg-white">
      <iframe
        srcDoc={srcDoc}
        sandbox="allow-same-origin"
        className="w-full h-full border-0"
        title="Preview"
      />
    </div>
  );
}
